import { calculate } from '@calculations/price/strategies/raf-fixed-market/calculate';
import { noOpUnitPrice } from '@ui/features/quote-builder/state/calculations/strategies/no-op';

import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  RecalculatePriceStrategy,
} from '../types';

export function onQuantityChange({
  state,
  quoteLine,
  quantity,
}: OnQuantityChangeInput): OnQuantityChangeOutput {
  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  if (!quoteLine.craftProductOptions) {
    return {
      status: 'failure',
      reason: 'No craft product options found',
    };
  }

  const output = calculate(
    {
      quantity,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
      productOptions: quoteLine.productOptionsUuids.map(
        (uuid) => quoteLine.productOptions[uuid],
      ),
      craftProductOptions: quoteLine.craftProductOptions,
    },
    state.calculationConfig,
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  return {
    status: 'success',
    unitPrice: output.unitPrice,
    unitPriceAddition: output.unitPriceAddition,
    changesUnitPrice: output.changesUnitPrice,
  };
}

export function onQuotePriceCreation({
  state,
  quoteLine,
  quotePrice,
}: OnQuotePriceCreationInput): OnQuotePriceCreationOutput {
  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  if (!quoteLine.craftProductOptions) {
    return {
      status: 'failure',
      reason: 'No craft product options found',
    };
  }

  const output = calculate(
    {
      quantity: quotePrice.quantity,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
      productOptions: quoteLine.productOptionsUuids.map(
        (uuid) => quoteLine.productOptions[uuid],
      ),
      craftProductOptions: quoteLine.craftProductOptions,
    },
    state.calculationConfig,
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice: output.unitPrice,
    },
  };
}

export const recalculateRAFFixedMarket = {
  onQuantityChange,
  onUnitPriceChange: noOpUnitPrice,
  onGrossMarginChange: null,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
